import { ref, Ref } from 'vue';
import { CookieStorageOptions, DEFAULT_COOKIE_OPTIONS } from '../services/utils/CookieStorage';

// Re-export domain utilities for backward compatibility
export { encodeDomainToCookieSuffix, getRootDomain, getSharedDomain } from './useCookieDomain';

// Default expiration in days (convert to seconds for CookieStorageOptions)
const DEFAULT_EXPIRATION_DAYS = 7;
const DAYS_TO_SECONDS = 24 * 60 * 60;
const SECONDS_TO_MILLISECONDS = 1000;
const DEFAULT_EXPIRATION_IN_SECONDS = DEFAULT_EXPIRATION_DAYS * DAYS_TO_SECONDS;

/**
 * Helper function to get a cookie by name
 */
const getCookieValue = <T = any>(name: string): T | null => {
  const match = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${name}=`));

  if (!match) {
    return null;
  }

  const separatorIndex = match.indexOf('=');
  if (separatorIndex < 0) {
    return null;
  }

  const value = match.slice(separatorIndex + 1);
  if (!value) {
    return null;
  }

  let decoded: string;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    return null;
  }

  try {
    const parsed = JSON.parse(decoded) as T;
    return parsed;
  } catch {
    return decoded as T;
  }
};

/**
 * Helper function to set a cookie
 */
const setCookieValue = (name: string, value: any, options: CookieStorageOptions): void => {
  let stringValue: string;

  if (typeof value === 'object' && value !== null) {
    // It's an object, stringify it
    stringValue = encodeURIComponent(JSON.stringify(value));
  } else {
    // It's a primitive (string, number, etc.), convert to string
    stringValue = encodeURIComponent(String(value));
  }

  const expires = new Date(Date.now() + options.maxAge * SECONDS_TO_MILLISECONDS).toUTCString();
  const sameSite = options.sameSite || 'lax';
  const path = options.path || '/';
  const secure = options.secure ? ';secure' : '';
  const domain = options.domain ? `;domain=${options.domain}` : '';

  document.cookie = `${name}=${stringValue};expires=${expires};path=${path};samesite=${sameSite}${secure}${domain}`;
};

/**
 * Helper function to remove a cookie
 */
const removeCookieValue = (name: string, options: Partial<CookieStorageOptions> = {}): void => {
  const path = options.path || '/';
  const domain = options.domain ? `;domain=${options.domain}` : '';
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}${domain}`;
};

/**
 * A generic composable function to manage cookies with support for objects
 * @param {string} cookieName - The name of the cookie to manage (should already include domain suffix if needed)
 * @param {number} expiresIn - Expiration time in seconds
 * @param {Partial<CookieStorageOptions>} cookieOptions - Additional cookie options
 * Reads `document.cookie` on each `.value` access, but external updates do not trigger reactive re-renders.
 * @returns {Ref} Reactive reference to the cookie value
 */
export const useCookie = <T extends Record<string, any> | string | undefined = string>(
  cookieName: string,
  expiresIn: number = DEFAULT_EXPIRATION_IN_SECONDS,
  cookieOptions: Partial<CookieStorageOptions> = {}
): Ref<T | undefined> => {
  const initialValue: undefined = undefined;

  // Create a reactive reference to hold the cookie value
  const cookie = ref<T | undefined>(initialValue);

  // Define a getter/setter for the value property
  Object.defineProperty(cookie, 'value', {
    get: () => {
      const value = getCookieValue<T>(cookieName);
      return value !== null ? value : initialValue;
    },
    set: (newValue: T | null | undefined) => {
      if (newValue !== null && newValue !== undefined) {
        const options: CookieStorageOptions = {
          ...DEFAULT_COOKIE_OPTIONS,
          maxAge: expiresIn,
          sameSite: 'lax',
          ...cookieOptions
        };
        setCookieValue(cookieName, newValue, options);
      } else {
        removeCookieValue(cookieName, cookieOptions);
      }
    }
  });

  return cookie as Ref<T | undefined>;
};

// Helper type to create object cookie interfaces
export type CookieObjectRef<T extends Record<string, any>> = Ref<T | undefined>;
